import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssetReviewItem, AssetReviewSnapshot } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../transport/context.js';

/**
 * The IN-APP asset-review surface — Foundry's first-class review panel, NOT
 * review.html opened in a browser.
 *
 * When the create flow reaches the asset-review stage, the GUI main pushes
 * `asset-review:ready` and the create dialog mounts this panel. The panel drives
 * the SAME engine review server the headless review.html talks to, via the
 * `art:*` IPC proxies (renderer → main → engine): one backend, two
 * frontends. (crucible#3007 — rewired off the cloud-unreachable
 * `asset-review:*` invoke verbs onto their `art:*` twins; the `asset-review:
 * ready`/`:closed` PUSH channels are unchanged.) For each generated asset the
 * taste-owner can:
 *
 *   - APPROVE it (✓),
 *   - REDIRECT it with free-text → regenerate ("same character, but …"),
 *   - or press DONE REVIEWING to ship what they've got.
 *
 * The panel reflects live status (the done-pill: approved / reviewable) by
 * polling the snapshot. When the engine reports `done` (everything approved or
 * Done pressed), `onDone` fires so the dialog can show the "finishing up"
 * state — create's `waitForApproval` observes the same engine done-signal and
 * proceeds to commit.
 *
 * Styling matches the renderer's dark theme (the `crucible-accent` token, the
 * `white/NN` opacity scale used across the create dialog + cards). It is
 * intentionally clean and not over-built: an asset grid + a verdict row per
 * card + a footer. Visual polish beyond this belongs to a later design pass.
 */

/** Poll cadence for the snapshot while the panel is open (ms). */
const POLL_INTERVAL_MS = 1500;

export interface AssetReviewPanelProps {
  /** kebab-case game id under review (shown in the header). */
  gameId: string;
  /** Fires once the engine reports the review is `done`. */
  onDone?: () => void;
}

export function AssetReviewPanel({ gameId, onDone }: AssetReviewPanelProps): React.JSX.Element {
  const client = useCrucibleClient();
  const [snapshot, setSnapshot] = useState<AssetReviewSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Per-asset busy flag (approve / regenerate in flight). */
  const [busyId, setBusyId] = useState<string | null>(null);
  /** The asset currently showing its redirect free-text box, keyed by id. */
  const [redirectFor, setRedirectFor] = useState<string | null>(null);
  const [redirectText, setRedirectText] = useState('');
  const [finishing, setFinishing] = useState(false);

  // Latch so `onDone` fires exactly once even though the poll keeps running.
  const doneFiredRef = useRef(false);
  // Tracks the last applied snapshot so a dropped poll doesn't banner an error
  // over a review that is already on screen and healthy.
  const snapshotRef = useRef<AssetReviewSnapshot | null>(null);
  // crucible#2801 / asset-forge#66 (Bugbot ec3440b3) — set the instant
  // `asset-review:closed` decides the terminal state (finished OR aborted).
  // The 1.5s poll keeps running independently of that push (it's a separate
  // effect) and, without this check, an in-flight or subsequently-scheduled
  // poll tick can still call `apply`/`setError` on a stale pre-teardown
  // snapshot — silently reverting `done: true` back to `false`, or wiping the
  // 'aborted' error the push just surfaced. Once terminal, the poll is
  // permanently a no-op: the push is authoritative, not just "first past the
  // post".
  const terminalRef = useRef(false);
  // Read onDone through a ref so an inline parent callback doesn't recreate
  // `apply` (and thereby tear down + restart the 1.5s poll) on every render.
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  const apply = useCallback((next: AssetReviewSnapshot) => {
    snapshotRef.current = next;
    setSnapshot(next);
    setError(null);
    if (next.done && !doneFiredRef.current) {
      doneFiredRef.current = true;
      onDoneRef.current?.();
    }
  }, []);

  // Initial load + poll. The snapshot is the single source the panel renders
  // from; polling keeps the done-pill + per-asset state live as the engine
  // mutates the manifest (a regenerate lands a fresh `staging` take, etc.).
  useEffect(() => {
    let cancelled = false;
    const tick = async (): Promise<void> => {
      // crucible#2801 / asset-forge#66 (Bugbot ec3440b3) — once
      // `asset-review:closed` has decided the terminal state, the poll must
      // never touch it again (see terminalRef's own doc).
      if (terminalRef.current) return;
      try {
        const snap = await client.invoke('art:snapshot', { gameId });
        if (!cancelled && !terminalRef.current) apply(snap);
      } catch (err) {
        // Only surface a poll failure before the first snapshot lands. Once a
        // snapshot is on screen, a single dropped poll is transient — don't flash
        // an error banner over a working review (it'll re-poll in POLL_INTERVAL_MS).
        if (!cancelled && !terminalRef.current && snapshotRef.current === null) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    };
    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [apply, client, gameId]);

  // crucible#2801 / asset-forge#66 — the poll above can only ever observe a
  // LAGGING view of "done": the review server can be torn down (a genuine
  // finish, or the surrounding build aborting) between two polls, and once
  // it's gone every subsequent poll throws "no active review" — a failure
  // the catch block above deliberately swallows once a snapshot has already
  // landed (so a single transient blip doesn't flash an error over a
  // healthy review). That silence meant a finish that raced ahead of this
  // panel's poll left it stuck forever, showing its last pre-done state with
  // no way to ever learn otherwise. `asset-review:closed` removes the race:
  // the main process pushes the terminal reason explicitly, at the exact
  // moment it tears the server down, so the panel reacts to a fact instead
  // of inferring one from an ambiguous poll failure.
  useEffect(() => {
    const unsubClosed = client.subscribe('asset-review:closed', (event) => {
      if (event.gameId !== gameId) return;
      // Set BEFORE applying the terminal state, not after — a poll tick
      // already in flight (its invoke call sent, awaiting a response) checks
      // this flag when it resolves, so it must see `true` in time to skip
      // its own apply()/setError() no matter which of the two settles last.
      terminalRef.current = true;
      if (event.reason === 'finished') {
        // A snapshot may not exist yet if the review closed before the
        // panel's very first poll ever landed — fire onDone directly in
        // that case (nothing to render `done: true` onto). Otherwise carry
        // the last known asset states forward, just flipping `done`, so the
        // panel's own done-pill/rendering stays consistent with how a
        // poll-observed `done: true` would have looked.
        if (snapshotRef.current) {
          apply({ ...snapshotRef.current, done: true });
        } else if (!doneFiredRef.current) {
          doneFiredRef.current = true;
          onDoneRef.current?.();
        }
      } else {
        // 'aborted' — the build failed/aborted for a reason unrelated to the
        // review itself. NEVER treat this as done (that would report a
        // failure as a fake success); surface it as a real, visible error.
        setError(
          'The build was interrupted before this review finished. Restart the build to try again.',
        );
      }
    });
    // Bugbot 9d2be0c3 (PR #2813 round 3) — `terminalRef` latches permanently
    // once set, so a build RETRY that re-launches a fresh review for this
    // SAME gameId (a real scenario: retry-after-abort/failure) would leave
    // the panel stuck showing the old terminal state forever, never polling
    // the new review. Mirrors `TvSteeringSurface.tsx`'s own established
    // pattern (its `asset-review:ready` handler resets its terminal latch on
    // a fresh ready for the same gameId) — a restarted review always gets a
    // fresh `asset-review:ready` push (see the launch wrapper in
    // crucible-gui/src/main/index.ts), so that's the correct un-latch signal.
    const unsubReady = client.subscribe('asset-review:ready', (event) => {
      if (event.gameId !== gameId) return;
      terminalRef.current = false;
      doneFiredRef.current = false;
      setError(null);
    });
    return () => {
      unsubClosed();
      unsubReady();
    };
  }, [apply, client, gameId]);

  const onApprove = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        const snap = await client.invoke('art:approve', {
          id,
          gameId,
        });
        apply(snap);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [apply, client, gameId],
  );

  const onRegenerate = useCallback(
    async (id: string, feedback: string) => {
      setBusyId(id);
      setRedirectFor(null);
      setRedirectText('');
      try {
        const snap = await client.invoke('art:regenerate', {
          id,
          feedback,
          gameId,
        });
        apply(snap);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [apply, client, gameId],
  );

  const onFinish = useCallback(async () => {
    setFinishing(true);
    try {
      const snap = await client.invoke('art:finish', { gameId });
      apply(snap);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setFinishing(false);
    }
  }, [apply, client, gameId]);

  return (
    <div className="flex flex-col gap-4 py-2" data-testid="asset-review-panel">
      {/* Header + live done-pill */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Review your game art</h2>
          <p className="text-sm text-white/50">Approve each piece, or ask for a change. {gameId}</p>
        </div>
        {snapshot && (
          <span
            className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80"
            data-testid="asset-review-done-pill"
          >
            {snapshot.approved}/{snapshot.reviewable} approved
            {snapshot.done ? ' · done' : ''}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/15 p-3 text-sm text-red-300" role="alert">
          {error}
        </div>
      )}

      {!snapshot && !error && (
        <div className="flex items-center gap-3 py-6 text-sm text-white/60">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
          Loading your generated art…
        </div>
      )}

      {/* Asset grid */}
      {snapshot && (
        <div className="grid max-h-[420px] grid-cols-2 gap-3 overflow-auto pr-1">
          {snapshot.assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              busy={busyId === asset.id}
              redirectOpen={redirectFor === asset.id}
              redirectText={redirectFor === asset.id ? redirectText : ''}
              onApprove={() => void onApprove(asset.id)}
              onOpenRedirect={() => {
                setRedirectFor(asset.id);
                setRedirectText('');
              }}
              onCancelRedirect={() => {
                setRedirectFor(null);
                setRedirectText('');
              }}
              onRedirectTextChange={setRedirectText}
              onSubmitRedirect={() => {
                const fb = redirectText.trim();
                if (fb) void onRegenerate(asset.id, fb);
              }}
            />
          ))}
        </div>
      )}

      {/* Footer — Done reviewing */}
      {snapshot && (
        <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
          <p className="text-xs text-white/40">
            {snapshot.done
              ? 'All set — finishing your game…'
              : 'Approve everything, or click Done when you’re happy.'}
          </p>
          <button
            type="button"
            data-testid="asset-review-finish"
            disabled={finishing || snapshot.done}
            onClick={() => void onFinish()}
            className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-black transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
          >
            {finishing || snapshot.done ? 'Finishing…' : 'Done reviewing'}
          </button>
        </div>
      )}
    </div>
  );
}

/** One asset card: image (or audio chip), label/status, and the verdict row. */
function AssetCard({
  asset,
  busy,
  redirectOpen,
  redirectText,
  onApprove,
  onOpenRedirect,
  onCancelRedirect,
  onRedirectTextChange,
  onSubmitRedirect,
}: {
  asset: AssetReviewItem;
  busy: boolean;
  redirectOpen: boolean;
  redirectText: string;
  onApprove: () => void;
  onOpenRedirect: () => void;
  onCancelRedirect: () => void;
  onRedirectTextChange: (v: string) => void;
  onSubmitRedirect: () => void;
}): React.JSX.Element {
  const approved = asset.status === 'approved';
  return (
    <div
      className="flex flex-col gap-2 rounded-xl border border-white/10 bg-black/30 p-3"
      data-testid={`asset-review-card-${asset.id}`}
      data-asset-status={asset.status}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-white/5">
        {/*
         * `imageUrl` is resolved by the shared `fetchAssetDataUrl` (asset-
         * review-proxy.ts), which as of crucible#2782 also resolves audio
         * content-types (needed for the Art tab's inspector player) — so it
         * is no longer image-only just because it's non-null. This build-
         * time panel has no audio player, so it must keep excluding audio
         * from the `<img>` branch explicitly, or a resolved audio take would
         * render as a broken image here instead of the existing glyph.
         */}
        {asset.category !== 'audio' && asset.imageUrl ? (
          <img
            src={asset.imageUrl}
            alt={asset.id}
            className="h-full w-full object-contain"
            // checkerboard-ish backdrop so transparent (keyed) art reads
            style={{
              backgroundImage:
                'linear-gradient(45deg,#222 25%,transparent 25%),linear-gradient(-45deg,#222 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#222 75%),linear-gradient(-45deg,transparent 75%,#222 75%)',
              backgroundSize: '16px 16px',
              backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-wide text-white/40">
            {asset.category === 'audio' ? '♪ audio' : asset.reviewable ? 'no preview' : 'not ready'}
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
          </div>
        )}
        {approved && (
          <span className="absolute right-1.5 top-1.5 rounded-full bg-green-500/90 px-2 py-0.5 text-[10px] font-semibold text-black">
            ✓ approved
          </span>
        )}
      </div>

      {/* Label */}
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-white/90">{asset.id}</p>
        <p className="truncate text-[11px] uppercase tracking-wide text-white/35">
          {asset.category}
          {asset.status !== 'approved' ? ` · ${asset.status}` : ''}
        </p>
      </div>

      {asset.bgRemovalDefect && (
        <div
          className="rounded-md border border-red-400/40 bg-red-500/15 px-2 py-1.5 text-xs font-medium text-red-200"
          role="alert"
        >
          Background not removed
        </div>
      )}

      {/* Verdict row OR redirect free-text */}
      {redirectOpen ? (
        <div className="flex flex-col gap-2">
          <textarea
            autoFocus
            value={redirectText}
            onChange={(e) => onRedirectTextChange(e.target.value)}
            placeholder="e.g. same character, but warmer light and a calmer pose"
            data-testid={`asset-review-redirect-input-${asset.id}`}
            className="h-16 w-full resize-none rounded-lg border border-white/15 bg-black/40 p-2 text-xs text-white/90 placeholder:text-white/30 focus:border-crucible-accent focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !redirectText.trim()}
              onClick={onSubmitRedirect}
              data-testid={`asset-review-redirect-submit-${asset.id}`}
              className="flex-1 rounded-lg bg-crucible-accent px-2 py-1.5 text-xs font-medium text-black transition hover:brightness-110 disabled:opacity-40"
            >
              Regenerate
            </button>
            <button
              type="button"
              onClick={onCancelRedirect}
              className="rounded-lg bg-white/10 px-2 py-1.5 text-xs text-white/70 transition hover:bg-white/15"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy || approved || !asset.reviewable}
            onClick={onApprove}
            data-testid={`asset-review-approve-${asset.id}`}
            className="flex-1 rounded-lg bg-white/10 px-2 py-1.5 text-xs font-medium text-white/90 transition hover:bg-green-500/20 disabled:opacity-40"
          >
            {approved ? 'Approved' : 'Approve'}
          </button>
          <button
            type="button"
            disabled={busy || !asset.reviewable}
            onClick={onOpenRedirect}
            data-testid={`asset-review-redirect-${asset.id}`}
            className="flex-1 rounded-lg bg-white/10 px-2 py-1.5 text-xs font-medium text-white/90 transition hover:bg-white/15 disabled:opacity-40"
          >
            ✏ Change
          </button>
        </div>
      )}
    </div>
  );
}
